import { useCallback, useMemo, useState } from 'react';
import {
  ONBOARDING_STEPS,
  doneCount,
  initialStepStates,
  progressLabel,
  type OnboardingStep,
  type OnboardingStepStates
} from '../models/onboarding';

/**
 * ViewModel for the fresh-org dashboard onboarding checklist (turn 12.3).
 *
 * Owns the per-step done/skipped state, the "Hide checklist" toggle, and the
 * derived progress label. The state is in-memory only — it mirrors the spec
 * mock, which does not persist across reloads (see `models/onboarding.ts`).
 *
 * `Dashboard.tsx` renders the checklist when `useDashboardVM` reports
 * `mode === 'onboarding'` and this VM's `hidden` is false; when `hidden` is
 * true the page falls back to the populated card stack plus a header
 * "Onboarding" link wired to `show()`.
 */
export interface UseOnboardingChecklistVM {
  /** The static step catalog, in display order. */
  steps: ReadonlyArray<OnboardingStep>;
  /** Per-step state keyed by step number. */
  stepStates: OnboardingStepStates;
  /** Count of steps marked done. */
  doneCount: number;
  /** `"2 of 5 done"` progress label. */
  progressLabel: string;
  /** True once the user clicked "Hide checklist". */
  hidden: boolean;
  /** Mark a step done — the "go"/CTA action. */
  markDone: (n: number) => void;
  /** Mark a step skipped — the "Skip" link action. */
  skip: (n: number) => void;
  /** Hide the checklist (fall back to the populated stack). */
  hide: () => void;
  /** Re-show the checklist (header "Onboarding" link). */
  show: () => void;
}

export function useOnboardingChecklistVM(): UseOnboardingChecklistVM {
  const [stepStates, setStepStates] = useState<OnboardingStepStates>(
    initialStepStates
  );
  const [hidden, setHidden] = useState(false);

  const markDone = useCallback((n: number) => {
    setStepStates((prev) => ({ ...prev, [n]: 'done' }));
  }, []);

  const skip = useCallback((n: number) => {
    setStepStates((prev) =>
      // A done step stays done — skipping only moves a `todo` step.
      prev[n] === 'done' ? prev : { ...prev, [n]: 'skipped' }
    );
  }, []);

  const hide = useCallback(() => setHidden(true), []);
  const show = useCallback(() => setHidden(false), []);

  const done = useMemo(() => doneCount(stepStates), [stepStates]);
  const label = useMemo(() => progressLabel(stepStates), [stepStates]);

  return {
    steps: ONBOARDING_STEPS,
    stepStates,
    doneCount: done,
    progressLabel: label,
    hidden,
    markDone,
    skip,
    hide,
    show
  };
}
